<template>
  <div class="student-answer wrapper" v-loading="pageLoading">
    <el-breadcrumb separator="/" class="bread-crumb">
      <el-breadcrumb-item :to="{ path: '/home/schedule' }">研修空间</el-breadcrumb-item>
      <el-breadcrumb-item>{{ pageTitle }}</el-breadcrumb-item>
    </el-breadcrumb>

    <div class="workspace">
      <!-- 左侧列表 -->
      <aside class="list-panel" v-loading="listLoading">
        <div class="list-filters filter-seg">
          <span
            v-for="f in filters"
            :key="f.value"
            class="filter-btn"
            :class="{ active: currentFilter === f.value }"
            @click="currentFilter = f.value"
          >{{ f.label }}</span>
        </div>
        <div class="list-scroll" ref="listScroll">
          <div class="list-scroll-inner">
          <template v-if="groupedCatalog.length">
            <div
              class="log-group"
              v-for="group in groupedCatalog"
              :key="group.id"
              :data-group-id="group.id"
              :class="{ 'is-expanded': expandedGroupId === group.id }"
            >
              <div class="log-group-hd" @click="toggleGroup(group.id)">
                <div class="log-group-hd-left">
                  <i class="el-icon-arrow-right group-arrow"></i>
                  <el-tooltip
                    :content="group.name"
                    :disabled="!isGroupNameFolded(group.name)"
                    popper-class="student-answer-tip"
                    placement="top"
                  >
                    <span class="log-group-title">{{ shortGroupName(group.name) }}</span>
                  </el-tooltip>
                </div>
                <span class="log-group-meta">{{ filteredItems(group.items).length }} 天</span>
              </div>
              <div class="log-group-bd" v-show="expandedGroupId === group.id">
                <div class="log-group-items">
                  <div
                    v-for="item in filteredItems(group.items)"
                    :key="item.id"
                    class="item-card"
                    :class="{
                      'is-active': currentKey === item.id,
                      'is-pending': item.status === 'pending'
                    }"
                    @click="openDetail(item)"
                  >
                    <div class="item-top">
                      <div class="item-date-col">
                        <span class="item-title">{{ formatDateLabel(item.dateKey) }}</span>
                        <span class="item-week">{{ weekdayLabel(item.dateKey) }}</span>
                        <span v-if="item.isToday" class="status-pill is-today">今日</span>
                      </div>
                      <span class="status-pill" :class="statusPillClass(item.status)">{{ statusLabel(item.status) }}</span>
                    </div>
                  </div>
                  <div v-if="!filteredItems(group.items).length" class="list-empty">暂无记录</div>
                </div>
              </div>
            </div>
          </template>
          <div v-else-if="!listLoading" class="list-empty">暂无记录</div>
          </div>
        </div>
      </aside>

      <!-- 右侧详情 -->
      <section class="detail-panel" v-loading="detailLoading">
        <div v-if="!currentItem" class="detail-empty">
          <span>请从左侧选择一条记录查看或作答</span>
        </div>

        <template v-else>
          <div class="detail-body" :class="{ 'is-editing': editMode }">
            <!-- 信息卡 -->
            <div class="info-card">
              <div class="info-grid">
                <div class="info-date-col">
                  <div class="info-date">{{ formatDateLabel(currentItem.dateKey) }}</div>
                  <div class="info-date-sub">
                    <span>{{ weekdayLabel(currentItem.dateKey) }}</span>
                    <span v-if="currentItem.isToday" class="status-pill is-today">今日</span>
                  </div>
                </div>
                <div class="info-main">
                  <div class="info-row-top">
                    <span class="info-setting-name">{{ currentItem.settingName }}</span>
                    <span class="status-pill" :class="statusPillClass(currentItem.status)">{{ statusLabel(currentItem.status) }}</span>
                  </div>
                  <div class="info-row-chips">
                    <span class="info-chip">{{ currentItem.windowLabel }}</span>
                    <span class="info-chip" v-if="module === 'journal'">不少于 {{ currentItem.minWords }} 字</span>
                    <span class="info-chip" v-if="module === 'journal' && currentItem.allowAttachment !== 0">需上传附件</span>
                    <span class="info-chip" v-else-if="module !== 'journal'">至少 {{ currentItem.minImageCount || 1 }} 张图片</span>
                    <span
                      class="info-chip"
                      v-if="module === 'journal' && showDailyName"
                    >{{ currentItem.name }}</span>
                  </div>
                </div>
              </div>
              <div v-if="currentItem.desc" class="info-req">
                <strong>作答要求：</strong>{{ currentItem.desc }}
              </div>
            </div>

            <!-- 日志作答 -->
            <template v-if="module === 'journal'">
              <div v-if="currentItem.status === 'pending'" class="hint-box">尚未开启，暂不可作答</div>
              <template v-else-if="editMode">
                <div class="answer-section">
                  <div class="section-title">我的作答</div>
                  <div class="answer-input-wrap">
                    <el-input
                      type="textarea"
                      class="answer-textarea"
                      v-model="draftContent"
                      :placeholder="'请填写' + currentItem.settingName + '...'"
                    ></el-input>
                  </div>
                  <div class="char-count">{{ draftContent.length }} 字</div>
                  <div class="attach-row">
                    <div class="file-chip" v-for="(f, idx) in draftFiles" :key="idx">
                      <i class="el-icon-paperclip"></i> {{ f.name }}<template v-if="f.size"> · {{ f.size }}</template>
                      <span class="rm" @click="removeFile(idx)"><i class="el-icon-close"></i></span>
                    </div>
                    <el-button
                      size="mini"
                      plain
                      icon="el-icon-plus"
                      @click="openFileUploader"
                      v-if="currentItem.allowAttachment !== 0"
                    >添加附件</el-button>
                    <span
                      v-if="currentItem.allowAttachment !== 0"
                      class="attach-required-tip"
                    >附件为必传项</span>
                    <div style="display:none;">
                      <uploadFile
                        ref="fileUploader"
                        name="logFile"
                        text="上传附件"
                        :list="[]"
                        AdjustTheType="ReviewManagement"
                        accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.zip,.rar,.7z,.jpg,.jpeg,.png,.gif,.mp4,.mov,.avi"
                        @fileUploaded="onSingleFileUploaded"
                      ></uploadFile>
                    </div>
                  </div>
                </div>
              </template>
              <template v-else>
                <template v-if="currentItem.status === 'submitted' && draftContent">
                  <div class="section">
                    <div class="section-title">日志内容</div>
                    <div class="answer-preview">{{ draftContent }}</div>
                  </div>
                  <div class="section" v-if="draftFiles && draftFiles.length">
                    <div class="section-title">附件</div>
                    <div class="attach-row">
                      <div class="file-chip" v-for="(f, idx) in draftFiles" :key="idx">
                        <a v-if="f.ossUrl" :href="f.ossUrl" target="_blank"><i class="el-icon-paperclip"></i> {{ f.name }}</a>
                        <template v-else><i class="el-icon-paperclip"></i> {{ f.name }}</template>
                        <template v-if="f.size"> · {{ f.size }}</template>
                      </div>
                    </div>
                  </div>
                </template>
                <div v-else class="hint-box">
                  {{ currentItem.status === 'unsubmitted' ? '该日未提交，提交时间段已结束' : '暂无内容' }}
                </div>
              </template>
            </template>

            <!-- 精彩瞬间作答 -->
            <template v-else>
              <div v-if="currentItem.status === 'pending'" class="hint-box">尚未开启，暂不可作答</div>
              <template v-else-if="editMode">
                <div class="answer-section answer-section--moment">
                  <div class="moment-caption">
                    <div class="section-title">说明（选填）</div>
                    <el-input
                      type="textarea"
                      v-model="draftCaption"
                      :rows="3"
                      placeholder="用一句话描述精彩瞬间..."
                    ></el-input>
                    <div class="char-count">{{ draftCaption.length }} 字</div>
                  </div>
                  <div class="moment-media">
                    <div class="section-title">图片 / 视频</div>
                    <div class="media-grid">
                      <div class="media-thumb" v-for="(m, idx) in draftMedia" :key="idx">
                        <img v-if="m.type === 'image'" :src="m.url" alt="" @click="previewMedia(m.url, 'image')" />
                        <video v-else :src="m.url" muted playsinline @click="previewMedia(m.url, 'video')"></video>
                        <span class="media-tag" v-if="m.type === 'video'">视频</span>
                        <span class="media-rm" @click="removeMedia(idx)"><i class="el-icon-close"></i></span>
                      </div>
                      <div class="media-add" @click="openMediaUploader">
                        <i class="el-icon-plus"></i>
                        <span>添加</span>
                      </div>
                    </div>
                    <div style="display:none;">
                      <uploadFile
                        ref="mediaUploader"
                        name="momentMedia"
                        text="上传图片/视频"
                        :list="[]"
                        AdjustTheType="ReviewManagement"
                        accept="image/*,video/*"
                        @uploadFile="onOssMediaUploaded"
                        @fileUploaded="onSingleMediaUploaded"
                      ></uploadFile>
                    </div>
                  </div>
                </div>
              </template>
              <template v-else>
                <template v-if="currentItem.status === 'submitted'">
                  <div class="section" v-if="draftCaption">
                    <div class="section-title">说明</div>
                    <div class="answer-preview">{{ draftCaption }}</div>
                  </div>
                  <div class="section">
                    <div class="section-title">媒体</div>
                    <div class="media-grid" v-if="draftMedia && draftMedia.length">
                      <div class="media-thumb" v-for="(m, idx) in draftMedia" :key="idx">
                        <img v-if="m.type === 'image'" :src="m.url" alt="" @click="previewMedia(m.url, 'image')" />
                        <video v-else :src="m.url" muted playsinline @click="previewMedia(m.url, 'video')"></video>
                        <span class="media-tag" v-if="m.type === 'video'">视频</span>
                      </div>
                    </div>
                    <div v-else class="hint-box">暂无图片/视频</div>
                  </div>
                </template>
                <div v-else class="hint-box">
                  {{ currentItem.status === 'unsubmitted' ? '该日未提交，提交时间段已结束' : '暂无内容' }}
                </div>
              </template>
            </template>
          </div>

          <!-- 底部操作栏 -->
          <div class="detail-actions" v-if="showActions">
            <el-button v-if="editMode" size="small" @click="cancelEdit">取消</el-button>
            <el-button
              v-if="editMode && currentItem.status !== 'submitted'"
              size="small"
              :loading="saving"
              @click="saveDraft"
            >保存草稿</el-button>
            <el-button
              v-if="editMode"
              size="small"
              type="primary"
              :loading="saving"
              @click="submit"
            >{{ currentItem.status === 'submitted' ? '保存修改' : (module === 'journal' ? '提交日志' : '提交精彩瞬间') }}</el-button>
            <el-button
              v-if="!editMode && currentItem.status === 'submitted' && currentItem.canAnswer"
              size="small"
              type="primary"
              @click="enableEdit"
            >编辑</el-button>
          </div>
        </template>
      </section>
    </div>

    <!-- 媒体预览 -->
    <div class="lightbox" :class="{ show: lightboxUrl }" @click.self="closeLightbox">
      <span class="lightbox-close" @click="closeLightbox"><i class="el-icon-close"></i></span>
      <img v-if="lightboxType === 'image'" :src="lightboxUrl" alt="预览" />
      <video v-else :src="lightboxUrl" controls autoplay class="lightbox-video"></video>
    </div>
  </div>
</template>

<script>
import {
  todayKey,
  parseDateTime,
  formatDateLabel,
  weekdayLabel,
  isImageExt,
  isVideoExt
} from '../manager/log-moment-shared';
import uploadFile from '../manager/components/upload-file.vue';

const STATUS_LABEL = {
  pending: '未开启',
  todo: '待完成',
  submitted: '已提交',
  unsubmitted: '未提交'
};

const GROUP_NAME_MAX = 12;

export default {
  name: 'StudentAnswer',
  data() {
    return {
      module: 'journal',
      pageLoading: false,
      listLoading: false,
      saving: false,
      settings: [],
      currentFilter: 'all',
      currentKey: null,
      currentItem: null,
      editMode: false,
      detailLoading: false,
      expandedGroupId: null,
      fillId: null,
      draftContent: '',
      draftFiles: [],
      draftCaption: '',
      draftMedia: [],
      lightboxUrl: '',
      lightboxType: 'image',
      filters: [
        { label: '全部', value: 'all' },
        { label: '已完成', value: 'done' },
        { label: '未完成', value: 'undone' }
      ]
    };
  },
  components: {
    uploadFile
  },
  computed: {
    currentProject() {
      return this.$store.state.user.currentProject || {};
    },
    pageTitle() {
      return this.module === 'journal' ? '我的日志' : '我的精彩瞬间';
    },
    showDailyName() {
      if (!this.currentItem || this.module !== 'journal') return false;
      const defaultName = `${this.currentItem.settingName} · ${this.currentItem.dateKey}`;
      return this.currentItem.name && this.currentItem.name !== defaultName;
    },
    groupedCatalog() {
      const today = todayKey();
      return this.settings.map(tpl => {
        const days = (tpl.days || []).slice().sort((a, b) => {
          const da = a.logDate || a.momentDate || '';
          const db = b.logDate || b.momentDate || '';
          return da.localeCompare(db);
        });
        const items = days.map(day => {
          const dateKey = day.logDate || day.momentDate || '';
          const dayId = day.dayId;
          const itemKey = `${tpl.templateId}|${dayId}`;
          const { status, canAnswer } = this.computeDayStatus(day, tpl, today);

          const dailyName = day.name || '';
          const name = this.module === 'highlight'
            ? tpl.name
            : (dailyName || `${tpl.name} · ${dateKey}`);

          return {
            id: itemKey,
            dayId,
            templateId: tpl.templateId,
            settingName: tpl.name,
            dateKey,
            name,
            desc: (this.module === 'journal' ? (day.description || tpl.description) : tpl.description) || '',
            status,
            canAnswer,
            isToday: dateKey === today,
            windowLabel: `${tpl.submitStart || '--'} ~ ${tpl.submitEnd || '--'}`,
            minWords: tpl.minWordCount || 50,
            allowAttachment: tpl.allowAttachment,
            minImageCount: tpl.minImageCount || 1
          };
        });
        return { id: tpl.templateId, name: tpl.name, items };
      });
    },
    showActions() {
      if (!this.currentItem) return false;
      if (this.editMode) return true;
      if (!this.editMode && this.currentItem.status === 'submitted' && this.currentItem.canAnswer) return true;
      return false;
    }
  },
  methods: {
    computeDayStatus(day, tpl, today) {
      const dateKey = day.logDate || day.momentDate || '';
      const now = new Date();
      const start = parseDateTime(tpl.submitStart);
      const end = parseDateTime(tpl.submitEnd);
      const inWindow = start && end ? (now >= start && now <= end) : false;
      const beforeStart = start ? now < start : false;
      const dateOpened = dateKey && dateKey <= today;
      const isSubmitted = this.module === 'journal'
        ? day.submitted === true
        : (day.answerStatus === 2 || day.submitted === true);

      let status;
      let canAnswer;
      // 精彩瞬间：日期已到且已过 submitStart 即可作答，过了 submitEnd 仍可补交、已提交仍可改
      if (this.module === 'highlight') {
        if (!dateOpened || beforeStart) {
          status = isSubmitted ? 'submitted' : 'pending';
          canAnswer = false;
        } else if (isSubmitted) {
          status = 'submitted';
          canAnswer = true;
        } else {
          status = 'todo';
          canAnswer = true;
        }
        return { status, canAnswer };
      }
      if (isSubmitted) {
        status = 'submitted';
        canAnswer = inWindow;
      } else if (!dateOpened || beforeStart) {
        status = 'pending';
        canAnswer = false;
      } else if (inWindow) {
        status = 'todo';
        canAnswer = true;
      } else {
        status = 'unsubmitted';
        canAnswer = false;
      }
      return { status, canAnswer };
    },
    formatDateLabel(dateKey) {
      return formatDateLabel(dateKey);
    },
    weekdayLabel(dateKey) {
      return weekdayLabel(dateKey);
    },
    statusLabel(status) {
      return STATUS_LABEL[status] || status;
    },
    statusPillClass(status) {
      const map = {
        pending: 'is-wait',
        todo: 'is-personal',
        submitted: 'is-done',
        unsubmitted: 'is-miss'
      };
      return map[status] || '';
    },
    shortGroupName(name) {
      if (!name) return '';
      return name.length > GROUP_NAME_MAX ? `${name.slice(0, GROUP_NAME_MAX)}...` : name;
    },
    isGroupNameFolded(name) {
      return name && name.length > GROUP_NAME_MAX;
    },
    toggleGroup(groupId) {
      if (this.expandedGroupId === groupId) {
        this.expandedGroupId = null;
      } else {
        this.expandedGroupId = groupId;
        this.$nextTick(() => this.pinExpandedGroup(groupId));
      }
    },
    pinExpandedGroup(groupId) {
      const scroll = this.$refs.listScroll;
      if (!scroll) return;
      const group = scroll.querySelector(`[data-group-id="${groupId}"]`);
      if (!group) return;
      const inner = scroll.querySelector('.list-scroll-inner');
      const innerOffset = inner ? inner.offsetTop : 0;
      scroll.scrollTop = group.offsetTop + innerOffset - 8;
    },
    filteredItems(items) {
      return items.filter(item => {
        if (this.currentFilter === 'done') return item.status === 'submitted';
        if (this.currentFilter === 'undone') return item.status !== 'submitted';
        return true;
      });
    },
    expandGroupForItem(item) {
      for (const group of this.groupedCatalog) {
        if (group.items.some(i => i.id === item.id)) {
          this.expandedGroupId = group.id;
          break;
        }
      }
    },
    async loadSettings() {
      this.listLoading = true;
      try {
        const projectId = this.currentProject.id;
        let res;
        if (this.module === 'journal') {
          res = await this.$Api.Log.getUserList({ projectId });
        } else {
          res = await this.$Api.Moment.getUserList({ projectId });
        }
        if (res && (res.code === 200 || res.code === 0)) {
          this.settings = res.data || [];
        }
      } catch (e) {
        console.error('loadSettings error:', e);
      } finally {
        this.listLoading = false;
      }
      this.$nextTick(() => {
        if (this.groupedCatalog.length && !this.expandedGroupId) {
          this.expandedGroupId = this.groupedCatalog[0].id;
        }
        this.autoOpen();
      });
    },
    async reloadList() {
      const projectId = this.currentProject.id;
      try {
        let res;
        if (this.module === 'journal') {
          res = await this.$Api.Log.getUserList({ projectId });
        } else {
          res = await this.$Api.Moment.getUserList({ projectId });
        }
        if (res && (res.code === 200 || res.code === 0)) {
          this.settings = res.data || [];
        }
      } catch (e) {
        // ignore
      }
    },
    autoOpen() {
      let prefer = null;
      for (const group of this.groupedCatalog) {
        for (const item of group.items) {
          if (item.canAnswer && item.status !== 'submitted') {
            prefer = item;
            break;
          }
        }
        if (prefer) break;
      }
      if (!prefer) {
        for (const group of this.groupedCatalog) {
          for (const item of group.items) {
            if (item.status !== 'pending') {
              prefer = item;
              break;
            }
          }
          if (prefer) break;
        }
      }
      if (!prefer && this.groupedCatalog.length) {
        prefer = this.groupedCatalog[0].items[0];
      }
      if (prefer) {
        this.expandGroupForItem(prefer);
        if (prefer.status !== 'pending') {
          this.openDetail(prefer);
        }
      }
    },
    async openDetail(item) {
      this.expandGroupForItem(item);
      if (item.status === 'pending') {
        this.currentKey = item.id;
        this.currentItem = item;
        this.editMode = false;
        this.fillId = null;
        this.draftContent = '';
        this.draftFiles = [];
        this.draftCaption = '';
        this.draftMedia = [];
        return;
      }

      this.currentKey = item.id;
      this.currentItem = item;
      this.detailLoading = true;

      try {
        let res;
        if (this.module === 'journal') {
          res = await this.$Api.Log.getFillDetail({ dayId: item.dayId });
        } else {
          res = await this.$Api.Moment.getFillDetail({ dayId: item.dayId });
        }
        if (res && (res.code === 200 || res.code === 0) && res.data) {
          const d = res.data;
          this.fillId = d.fillId || null;

          if (this.module === 'journal') {
            this.draftContent = d.content || '';
            this.draftFiles = (d.files || []).map((f, i) => ({
              id: f.id,
              name: f.fileName,
              fileName: f.fileName,
              ossUrl: f.ossUrl,
              ext: f.ext,
              fileSize: f.fileSize,
              sort: f.sort != null ? f.sort : i,
              size: f.fileSize > 1024 * 1024
                ? `${(f.fileSize / 1024 / 1024).toFixed(1)} MB`
                : `${Math.max(1, Math.round(f.fileSize / 1024))} KB`
            }));

            if (d.submitStatus === 1) {
              this.currentItem = { ...item, status: 'submitted', content: d.content, files: this.draftFiles };
            }
          } else {
            this.draftCaption = d.content || '';
            this.draftMedia = (d.files || []).map((f, i) => ({
              id: f.id,
              type: f.mediaType === 2 ? 'video' : 'image',
              mediaType: f.mediaType,
              url: f.ossUrl,
              name: f.fileName,
              ossUrl: f.ossUrl,
              ext: f.ext,
              fileSize: f.fileSize,
              sort: f.sort != null ? f.sort : i
            }));

            if (d.submitStatus === 1) {
              this.currentItem = { ...item, status: 'submitted', caption: d.content, media: this.draftMedia };
            }
          }
        }
      } catch (e) {
        console.error('openDetail error:', e);
      } finally {
        this.detailLoading = false;
      }

      const wantEdit = this.currentItem.status !== 'pending'
        && this.currentItem.status !== 'submitted'
        && this.currentItem.canAnswer;
      this.editMode = wantEdit;
    },
    enableEdit() {
      if (!this.currentItem || !this.currentItem.canAnswer) {
        this.$message.warning(
          this.currentItem && this.currentItem.status === 'pending'
            ? '尚未开启，暂不可作答'
            : '提交时间段已结束，无法编辑'
        );
        return;
      }
      this.editMode = true;
    },
    cancelEdit() {
      if (this.currentItem && this.currentItem.status === 'submitted') {
        this.editMode = false;
      } else {
        this.currentKey = null;
        this.currentItem = null;
        this.editMode = false;
      }
    },
    openFileUploader() {
      const uploader = this.$refs.fileUploader;
      if (uploader) {
        uploader.openDrawer(true);
      }
    },
    onSingleFileUploaded(payload) {
      const { identifier, fileName, fileSize } = payload;
      if (!identifier) return;
      const url = this.downloadURL + identifier;
      const ext = (fileName || '').split('.').pop() || '';
      const bytes = fileSize || 0;
      const sizeLabel = bytes > 1024 * 1024
        ? `${(bytes / 1024 / 1024).toFixed(1)} MB`
        : `${Math.max(1, Math.round(bytes / 1024))} KB`;
      this.draftFiles.push({
        fileName, name: fileName, ossUrl: url, ext, fileSize: bytes, size: sizeLabel, sort: this.draftFiles.length
      });
    },
    removeFile(idx) {
      this.draftFiles.splice(idx, 1);
    },
    openMediaUploader() {
      const uploader = this.$refs.mediaUploader;
      if (uploader) {
        uploader.openDrawer(true);
      }
    },
    onOssMediaUploaded() {
      // uploadFile 组件的 @uploadFile 回调（批量信息），暂不处理
    },
    onSingleMediaUploaded(payload) {
      const { identifier, fileName } = payload;
      if (!identifier) return;
      const url = this.downloadURL + identifier;
      const ext = ((fileName || '').split('.').pop() || '').toLowerCase();
      let type = '';
      if (isVideoExt(ext, fileName)) {
        type = 'video';
      } else if (isImageExt(ext, fileName)) {
        type = 'image';
      }
      if (!type) {
        this.$message.warning(`不支持的文件类型：${ext}，请上传图片或视频`);
        return;
      }
      this.draftMedia.push({ type, mediaType: type === 'video' ? 2 : 1, url, ossUrl: url, name: fileName, identifier });
    },
    removeMedia(idx) {
      this.draftMedia.splice(idx, 1);
    },
    previewMedia(url, type) {
      this.lightboxUrl = url;
      this.lightboxType = type || 'image';
    },
    closeLightbox() {
      this.lightboxUrl = '';
      this.lightboxType = 'image';
    },
    async saveDraft() {
      if (!this.currentItem || !this.currentItem.canAnswer) {
        this.$message.warning('不在提交时间段内，无法保存');
        return;
      }
      if (this.module === 'journal') {
        if (!this.draftContent.trim() && !this.draftFiles.length) {
          this.$message.warning('请先填写内容或添加附件');
          return;
        }
        if (this.currentItem.allowAttachment !== 0 && !this.draftFiles.length) {
          this.$message.warning('请添加附件后再保存');
          return;
        }
      } else {
        if (!this.draftCaption.trim() && !this.draftMedia.length) {
          this.$message.warning('请先添加媒体或填写说明');
          return;
        }
      }
      this.saving = true;
      try {
        await this.persistAnswer(0);
        this.$message.success('草稿已保存');
      } catch (e) {
        this.$message.error(e.message || '保存失败');
      } finally {
        this.saving = false;
      }
    },
    async submit() {
      if (!this.currentItem || !this.currentItem.canAnswer) {
        this.$message.warning('不在提交时间段内，无法提交');
        return;
      }
      if (this.module === 'journal') {
        const text = this.draftContent.trim();
        if (!text) {
          this.$message.warning(`请填写${this.currentItem.settingName}`);
          return;
        }
        if (text.length < this.currentItem.minWords) {
          this.$message.warning(`不少于 ${this.currentItem.minWords} 字（当前 ${text.length} 字）`);
          return;
        }
        if (this.currentItem.allowAttachment !== 0 && !this.draftFiles.length) {
          this.$message.warning('请添加附件后再提交');
          return;
        }
      } else {
        const imgCount = this.draftMedia.filter(m => m.type === 'image').length;
        const minImg = this.currentItem.minImageCount || 1;
        if (imgCount < minImg) {
          this.$message.warning(`至少上传 ${minImg} 张图片（当前 ${imgCount} 张）`);
          return;
        }
      }
      this.saving = true;
      try {
        await this.persistAnswer(1);
        this.$message.success(this.currentItem.status === 'submitted' ? '修改已保存' : '提交成功');
        await this.reloadList();
        const found = this.groupedCatalog.flatMap(g => g.items).find(i => i.id === this.currentKey);
        if (found) {
          await this.openDetail(found);
        }
        this.editMode = false;
      } catch (e) {
        this.$message.error(e.message || '提交失败');
      } finally {
        this.saving = false;
      }
    },
    async persistAnswer(submitStatus) {
      const item = this.currentItem;
      const dayId = item.dayId;
      const isEdit = !!this.fillId;

      let body;
      if (this.module === 'journal') {
        body = {
          dayId,
          content: this.draftContent,
          submitStatus,
          files: this.draftFiles.map((f, i) => ({
            ...(f.id ? { id: f.id } : {}),
            fileName: f.fileName || f.name,
            ossUrl: f.ossUrl || '',
            ext: f.ext || (f.name || '').split('.').pop() || '',
            fileSize: f.fileSize || 0,
            sort: i
          }))
        };
        const api = isEdit ? this.$Api.Log.editFill : this.$Api.Log.saveFill;
        const res = await api(body);
        if (res && (res.code === 200 || res.code === 0)) {
          this.fillId = res.data || this.fillId;
        } else {
          throw new Error(res?.msg || '保存失败');
        }
      } else {
        body = {
          dayId,
          content: this.draftCaption,
          submitStatus,
          files: this.draftMedia.map((m, i) => ({
            ...(m.id ? { id: m.id } : {}),
            mediaType: m.mediaType || (m.type === 'video' ? 2 : 1),
            fileName: m.name || m.fileName || '',
            ossUrl: m.ossUrl || m.url || '',
            ext: m.ext || (m.name || '').split('.').pop() || '',
            fileSize: m.fileSize || 0,
            sort: i
          }))
        };
        const api = isEdit ? this.$Api.Moment.editFill : this.$Api.Moment.saveFill;
        const res = await api(body);
        if (res && (res.code === 200 || res.code === 0)) {
          this.fillId = res.data || this.fillId;
        } else {
          throw new Error(res?.msg || '保存失败');
        }
      }
    }
  },
  created() {
    const m = (this.$route.query.module || 'journal').toLowerCase();
    this.module = (m === 'highlight' || m === 'moment') ? 'highlight' : 'journal';
  },
  mounted() {
    this.loadSettings();
  }
};
</script>

<style lang="less" scoped>
.student-answer {
  min-height: calc(100vh - 120px);
  padding: 20px 25px 24px;
}

.bread-crumb {
  margin-bottom: 14px;
  font-size: 13px;
}

.workspace {
  display: grid;
  grid-template-columns: 320px 1fr;
  gap: 14px;
  height: calc(100vh - 220px);
  min-height: 560px;
  align-items: stretch;
}

/* 左侧列表 */
.list-panel {
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 1px 8px rgba(0, 0, 0, 0.06);
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow: visible;
}

.filter-seg {
  display: flex;
  gap: 4px;
  padding: 10px;
  border-bottom: 1px solid #EBEEF5;
  background: #F0F2F5;
  flex-shrink: 0;

  .filter-btn {
    flex: 1;
    text-align: center;
    padding: 6px 8px;
    border-radius: 6px;
    font-size: 13px;
    color: #5C6673;
    background: transparent;
    border: none;
    cursor: pointer;
    transition: all .15s;

    &:hover {
      color: #3A7BD5;
    }

    &.active {
      background: #fff;
      color: #3A7BD5;
      font-weight: 500;
      box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);
    }
  }
}

.list-scroll {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  min-height: 0;
  -webkit-overflow-scrolling: touch;
}

.list-scroll-inner {
  padding: 8px;
}

.list-empty {
  padding: 40px 20px;
  text-align: center;
  color: #8B95A1;
  font-size: 13px;
}

.log-group {
  margin-bottom: 8px;
  border: 1px solid #EBEEF5;
  border-radius: 8px;
  overflow: visible;

  &.is-expanded .log-group-hd .group-arrow {
    transform: rotate(90deg);
  }

  &.is-expanded .log-group-hd {
    position: -webkit-sticky;
    position: sticky;
    top: 0;
    z-index: 5;
    margin: 0 -8px;
    padding-left: 20px;
    padding-right: 20px;
    background: #fff;
    border-bottom: 1px solid #EBEEF5;
    box-shadow: 0 1px 0 #EBEEF5;

    &::before {
      content: '';
      position: absolute;
      left: 0;
      right: 0;
      bottom: -4px;
      height: 4px;
      background: #fff;
      z-index: 1;
    }

    &:hover {
      background: #FAFBFC;

      &::before {
        background: #FAFBFC;
      }
    }
  }

  .log-group-hd {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 10px 12px;
    background: #FAFBFC;
    border-bottom: 1px solid #EBEEF5;
    cursor: pointer;
    user-select: none;

    .log-group-hd-left {
      display: flex;
      align-items: center;
      gap: 6px;
      min-width: 0;
      flex: 1;
    }

    .group-arrow {
      font-size: 12px;
      color: #8B95A1;
      transition: transform .15s;
      flex-shrink: 0;
    }

    .log-group-title {
      font-size: 15px;
      font-weight: 600;
      color: #1F2A37;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .log-group-meta {
      font-size: 12px;
      color: #8B95A1;
      flex-shrink: 0;
      margin-left: 8px;
    }
  }

  .log-group-bd {
    background: #fff;
  }

  .log-group-items {
    padding-left: 2em;
    padding: 4px 4px 4px 2em;
  }
}

.item-card {
  padding: 8px 10px;
  border-radius: 6px;
  cursor: pointer;
  border: 1px solid transparent;
  margin-bottom: 4px;
  transition: all .15s;

  &:hover {
    background: #F3F8FF;
  }

  &.is-active {
    background: #fff;
    border-color: #8BB8F0;
    box-shadow:
      inset 3px 0 0 #3A7BD5,
      0 0 0 1px rgba(58, 123, 213, 0.28),
      0 8px 18px rgba(58, 123, 213, 0.1);

    .item-title,
    .item-week {
      color: #3A7BD5;
    }
  }

  &.is-pending {
    .item-title,
    .item-week {
      color: #B0B6BF;
    }
  }

  .item-top {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
  }

  .item-date-col {
    display: flex;
    align-items: center;
    gap: 6px;
    min-width: 0;
    flex-wrap: wrap;
  }

  .item-title {
    font-size: 14px;
    font-weight: 400;
    color: #5C6673;
    line-height: 1.4;
  }

  .item-week {
    font-size: 13px;
    color: #8B95A1;
  }
}

/* 状态 pill */
.status-pill {
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 12px;
  font-weight: 500;
  flex-shrink: 0;
  white-space: nowrap;
  line-height: 1.4;

  &.is-wait {
    background: #F4F6F8;
    color: #8B95A1;
  }

  &.is-personal {
    background: #FFF7E8;
    color: #C48A1A;
  }

  &.is-done {
    background: #EEF8F0;
    color: #3D9A5A;
  }

  &.is-miss {
    background: #FDECEC;
    color: #C45656;
  }

  &.is-today {
    background: #F3F8FF;
    color: #3A7BD5;
  }
}

/* 右侧详情 */
.detail-panel {
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 1px 8px rgba(0, 0, 0, 0.06);
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;
}

.detail-empty {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #8B95A1;
  padding: 40px;
  font-size: 14px;
}

.detail-body {
  flex: 1;
  overflow-y: auto;
  padding: 16px 20px;
  min-height: 0;

  &.is-editing {
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }
}

.detail-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 12px 20px;
  border-top: 1px solid #EBEEF5;
  background: #FAFBFC;
  flex-shrink: 0;
}

/* 信息卡 */
.info-card {
  background: #FAFBFC;
  border: 1px solid #EBEEF5;
  border-radius: 8px;
  padding: 10px 12px;
  margin-bottom: 10px;
  flex-shrink: 0;

  .info-grid {
    display: grid;
    grid-template-columns: auto 1fr;
    gap: 12px;
    align-items: start;
  }

  .info-date-col {
    padding-right: 12px;
    border-right: 1px solid #EBEEF5;
    min-width: 72px;
  }

  .info-date {
    font-size: 18px;
    font-weight: 600;
    color: #1F2A37;
    line-height: 24px;
  }

  .info-date-sub {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 13px;
    color: #8B95A1;
    line-height: 22px;
    margin-top: 2px;
  }

  .info-main {
    display: flex;
    flex-direction: column;
    gap: 4px;
    min-width: 0;
  }

  .info-row-top {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    height: 24px;
  }

  .info-setting-name {
    font-size: 14px;
    font-weight: 600;
    color: #1F2A37;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .info-row-chips {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    align-items: center;
    height: 22px;
  }

  .info-chip {
    display: inline-flex;
    align-items: center;
    height: 22px;
    padding: 0 8px;
    font-size: 12px;
    color: #5C6673;
    background: #fff;
    border: 1px solid #EBEEF5;
    border-radius: 4px;
    white-space: nowrap;
  }

  .info-req {
    font-size: 13px;
    color: #5C6673;
    line-height: 1.6;
    padding-top: 8px;
    margin-top: 8px;
    border-top: 1px solid #EBEEF5;

    strong {
      color: #1F2A37;
      font-weight: 500;
    }
  }
}

/* 作答区 */
.answer-section {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;

  &.answer-section--moment {
    overflow-y: auto;
  }

  .section-title {
    font-size: 14px;
    font-weight: 600;
    margin-bottom: 8px;
    color: #1F2A37;
    flex-shrink: 0;
  }
}

.answer-input-wrap {
  flex: 1;
  display: flex;
  min-height: 0;
}

.answer-textarea {
  flex: 1;
  display: flex;

  /deep/ .el-textarea {
    flex: 1;
    display: flex;
  }

  /deep/ .el-textarea__inner {
    flex: 1;
    height: 100% !important;
    min-height: 120px;
    resize: none;
    overflow-y: auto;
  }
}

.section {
  margin-bottom: 18px;
  max-width: 100%;
  min-width: 0;

  .section-title {
    font-size: 14px;
    font-weight: 600;
    margin-bottom: 8px;
    color: #1F2A37;
  }
}

.char-count {
  text-align: right;
  font-size: 12px;
  color: #8B95A1;
  margin-top: 6px;
  flex-shrink: 0;
}

.answer-preview {
  padding: 12px 14px;
  background: #FAFBFC;
  border-radius: 8px;
  border: 1px solid #EBEEF5;
  white-space: pre-wrap;
  line-height: 1.75;
  font-size: 14px;
  color: #1F2A37;
  max-width: 100%;
  box-sizing: border-box;
  overflow-wrap: anywhere;
  word-break: break-word;
}

/* 附件 */
.attach-row {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 10px;
  align-items: center;
  flex-shrink: 0;

  .file-chip {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 5px 10px;
    background: #F4F6F8;
    border: 1px solid #EBEEF5;
    border-radius: 6px;
    font-size: 12px;
    color: #5C6673;

    .rm {
      width: 16px;
      height: 16px;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      font-size: 12px;
      color: #8B95A1;

      &:hover {
        background: #FDECEC;
        color: #C45656;
      }
    }
  }

  .attach-required-tip {
    font-size: 12px;
    color: #C48A1A;
    line-height: 1;
  }
}

/* 媒体 */
.media-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(110px, 1fr));
  gap: 10px;
  margin-top: 8px;

  .media-thumb {
    position: relative;
    aspect-ratio: 1;
    border-radius: 8px;
    background: #F4F6F8;
    border: 1px solid #EBEEF5;

    img, video {
      width: 100%;
      height: 100%;
      object-fit: cover;
      cursor: pointer;
      display: block;
      border-radius: 8px;
    }

    .media-tag {
      position: absolute;
      left: 5px;
      bottom: 5px;
      padding: 1px 5px;
      background: rgba(0, 0, 0, .55);
      color: #fff;
      font-size: 11px;
      border-radius: 3px;
      pointer-events: none;
    }

    .media-rm {
      position: absolute;
      top: -6px;
      right: -6px;
      width: 20px;
      height: 20px;
      border-radius: 50%;
      background: #8B95A1;
      color: #fff;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 12px;
      cursor: pointer;
      z-index: 1;
      box-shadow: 0 1px 4px rgba(0, 0, 0, .2);
      transition: background .15s;

      &:hover {
        background: #C45656;
      }
    }
  }

  .media-add {
    aspect-ratio: 1;
    border: 1px dashed #EBEEF5;
    border-radius: 8px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 6px;
    color: #8B95A1;
    font-size: 12px;
    background: #FAFBFC;
    cursor: pointer;
    transition: all .2s;

    i {
      font-size: 20px;
    }

    &:hover {
      border-color: #3A7BD5;
      color: #3A7BD5;
      background: #F3F8FF;
    }
  }
}

/* 提示框 */
.hint-box {
  padding: 28px;
  text-align: center;
  color: #8B95A1;
  background: #FAFBFC;
  border-radius: 8px;
  border: 1px dashed #EBEEF5;
  font-size: 13px;
}

/* 图片预览 */
.lightbox {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, .75);
  display: none;
  align-items: center;
  justify-content: center;
  z-index: 3000;
  cursor: zoom-out;

  &.show {
    display: flex;
  }

  .lightbox-close {
    position: absolute;
    top: 20px;
    right: 30px;
    font-size: 28px;
    color: #fff;
    cursor: pointer;
    z-index: 1;
    line-height: 1;

    &:hover {
      opacity: .7;
    }
  }

  img, .lightbox-video {
    max-width: 90vw;
    max-height: 90vh;
    border-radius: 4px;
    display: block;
    cursor: default;
  }
}
</style>

<style lang="less">
.student-answer-tip {
  background: #1F2A37 !important;
  color: #fff !important;
  font-size: 12px;
  padding: 6px 10px;
  border: none;

  .popper__arrow::after {
    border-top-color: #1F2A37 !important;
  }
}
</style>
